import { esCelularOTablet } from '../utils/dispositivo';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Tip } from './ui/tip';
import { XMarkIcon, BoltIcon, CameraIcon, PencilIcon } from '@heroicons/react/24/outline';
import { cargarOrt, crearSesion, backendActivo } from '../lib/ortRuntime';

/**
 * Escáner de odómetro EN VIVO: apuntás la cámara al tablero y el kilometraje
 * aparece como sugerencia. Corre 100% en el navegador con dos modelos ONNX
 * locales entrenados en el repo Svetovid sobre TRODO (etapa 1 detecta el
 * display, etapa 2 lee los dígitos sobre el recorte). La lectura SIEMPRE es
 * una sugerencia: el campo queda editable para que el operario confirme.
 */

const CLASES_DIGITOS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'X', 'punto'];
const DISPLAY_SIZE = 640;
const DISPLAY_ANCHORS = 8400;
const DIGITOS_SIZE = 320;
const DIGITOS_ANCHORS = 2100;

let ortModulo: any = null;
let sesionesPromise: Promise<{ display: any; digitos: any }> | null = null;

async function cargarSesiones() {
  if (!sesionesPromise) {
    sesionesPromise = (async () => {
      const ort = await cargarOrt();
      ortModulo = ort;
      // WebGPU si el equipo puede, wasm de respaldo (ver lib/ortRuntime)
      const [display, digitos] = await Promise.all([
        crearSesion(ort, '/models/odometro/display.onnx'),
        crearSesion(ort, '/models/odometro/digitos.onnx')
      ]);
      console.info(`[OdometerScanner] modelos listos (backend: ${backendActivo})`);
      return { display, digitos };
    })().catch((e) => {
      sesionesPromise = null; // permitir reintento si fallo la descarga
      throw e;
    });
  }
  return sesionesPromise;
}

/** Precarga modelos + runtime en segundo plano para que abrir sea instantaneo */
export function precalentarEscanerOdometro() {
  cargarSesiones().catch(() => { /* se reintenta al abrir */ });
}

interface Caja {
  x1: number; y1: number; x2: number; y2: number; score: number; cls: number;
}

// decode YOLOv8 crudo [1, 4+nc, N] + NMS AGNOSTICA de clase (el modelo duda
// 8-vs-5 y tira las dos cajas en el mismo lugar; queda la de mayor confianza)
function decodeYolo(data: Float32Array, nc: number, nAnchors: number, confMin: number): Caja[] {
  const cajas: Caja[] = [];
  for (let a = 0; a < nAnchors; a++) {
    let mejor = 0;
    let mejorC = -1;
    for (let c = 0; c < nc; c++) {
      const s = data[(4 + c) * nAnchors + a];
      if (s > mejor) { mejor = s; mejorC = c; }
    }
    if (mejor < confMin) continue;
    const cx = data[a];
    const cy = data[nAnchors + a];
    const bw = data[2 * nAnchors + a];
    const bh = data[3 * nAnchors + a];
    cajas.push({ x1: cx - bw / 2, y1: cy - bh / 2, x2: cx + bw / 2, y2: cy + bh / 2, score: mejor, cls: mejorC });
  }
  cajas.sort((p, q) => q.score - p.score);
  const out: Caja[] = [];
  const iou = (a: Caja, b: Caja) => {
    const ix = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1));
    const iy = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1));
    const inter = ix * iy;
    const ua = (a.x2 - a.x1) * (a.y2 - a.y1) + (b.x2 - b.x1) * (b.y2 - b.y1) - inter;
    return ua > 0 ? inter / ua : 0;
  };
  for (const c of cajas) {
    if (!out.some((o) => iou(o, c) > 0.45)) out.push(c);
  }
  return out;
}

interface Char { x: number; x2: number; w: number; cy: number; h: number; ch: string }

// De las cajas de caracteres al numero del odometro:
// 1) filas por Y (tolerancia sobre la altura MEDIANA, la maxima pegaba renglones)
// 2) cada fila se parte en segmentos donde hay un hueco horizontal grande
//    (odometro | trip en la misma linea)
// 3) gana el segmento con mas digitos (empate: el mas alto); 'punto' corta
//    (lo que sigue son decimas); ceros a la izquierda fuera
export function leerKilometraje(chars: Char[]): string {
  if (!chars.length) return '';
  const hs = chars.map((c) => c.h).sort((a, b) => a - b);
  const hMed = hs[Math.floor(hs.length / 2)] || 1;
  const filas: { cy: number; items: Char[] }[] = [];
  for (const c of [...chars].sort((p, q) => p.cy - q.cy)) {
    const fila = filas.find((f) => Math.abs(f.cy - c.cy) < hMed * 0.55);
    if (fila) fila.items.push(c);
    else filas.push({ cy: c.cy, items: [c] });
  }
  const segmentos: Char[][] = [];
  for (const f of filas) {
    const items = f.items.sort((p, q) => p.x - q.x);
    const ws = items.map((i) => i.w).sort((a, b) => a - b);
    const wMed = ws[Math.floor(ws.length / 2)] || 1;
    let seg: Char[] = [items[0]];
    for (let i = 1; i < items.length; i++) {
      if (items[i].x - items[i - 1].x2 > wMed * 2.0) { segmentos.push(seg); seg = []; }
      seg.push(items[i]);
    }
    if (seg.length) segmentos.push(seg);
  }
  const puntuar = (s: Char[]) => s.filter((i) => /\d/.test(i.ch)).length * 1000 + Math.max(...s.map((i) => i.h));
  segmentos.sort((a, b) => puntuar(b) - puntuar(a));
  let cs = (segmentos[0] || []).map((i) => i.ch);
  const iPunto = cs.indexOf('punto');
  if (iPunto >= 0) cs = cs.slice(0, iPunto);
  return cs.filter((c) => /\d/.test(c)).join('').replace(/^0+(?=\d)/, '');
}

interface Props {
  open: boolean;
  onClose: () => void;
  onKm: (km: number) => void;
  /** Ultimo km conocido del vehiculo (ficha): valida que la lectura sea plausible */
  kmReferencia?: number;
}

// Gramatica de la lectura: 2 a 7 digitos (hay camiones con el millon dado);
// si conocemos el km de la ficha, ademas tiene que ser plausible — esto mata
// errores sistematicos como leer la P de la caja como un 9 (427712P -> 4277129)
export function esKmPlausible(km: string, kmReferencia?: number): boolean {
  if (km.length < 2 || km.length > 7) return false;
  const n = Number(km);
  if (kmReferencia && kmReferencia > 0) {
    if (n > kmReferencia + 400000) return false; // 10x la ficha = caracter fantasma
    if (n < kmReferencia * 0.25) return false; // perdio digitos adelante
  }
  return true;
}

const OdometerScanner: React.FC<Props> = ({ open, onClose, onKm, kmReferencia }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const vivoRef = useRef(false);
  const [estado, setEstado] = useState<'cargando' | 'buscando' | 'detectado' | 'error'>('cargando');
  const [mensajeError, setMensajeError] = useState('');
  const [lectura, setLectura] = useState('');
  const [cuadro, setCuadro] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [conLinterna, setConLinterna] = useState(false);
  const [linternaPrendida, setLinternaPrendida] = useState(false);
  const ultimaLecturaRef = useRef<{ km: string; veces: number }>({ km: '', veces: 0 });
  // Carga manual: por si no detecta nada o detecta cualquier cosa
  const [manualAbierto, setManualAbierto] = useState(false);
  const manualAbiertoRef = useRef(false);
  const [manualValor, setManualValor] = useState('');
  // Dataset para reentrenar (Svetovid): la foto del tablero + el km final
  const ultimaCrudaRef = useRef('');
  // El telefono puede estar girado (apaisado, o con la rotacion bloqueada):
  // si no aparece el display se prueba el cuadro girado 90/180/270
  const rotacionRef = useRef(0);
  const sinDeteccionRef = useRef(0);
  // Zoom para no tener que meterse adentro del auto: nativo si el equipo lo
  // expone, si no digital (recorte del centro del video, mismo efecto)
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const zoomDigitalRef = useRef(true);
  const [zoomDigital, setZoomDigital] = useState(true);
  // Niveles segun lo que declare la camara; sin zoom nativo, digitales 1/2/4
  const [nivelesZoom, setNivelesZoom] = useState<number[]>([1, 2, 4]);

  const aplicarZoom = useCallback(async (nivel: number) => {
    // Nativo primero (mejor calidad); si el equipo no lo soporta, digital
    setZoom(nivel);
    zoomRef.current = nivel;
    const track = streamRef.current?.getVideoTracks?.()[0];
    const caps = (track as any)?.getCapabilities?.();
    if (track && caps?.zoom && nivel >= (caps.zoom.min || 1) && nivel <= caps.zoom.max) {
      try {
        await (track as any).applyConstraints({ advanced: [{ zoom: nivel }] });
        zoomDigitalRef.current = false;
        setZoomDigital(false);
        return;
      } catch { /* cae a digital */ }
    }
    zoomDigitalRef.current = true;
    setZoomDigital(true);
  }, []);

  const apagarTodo = useCallback(() => {
    vivoRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Foto del tablero completo (para el dataset: sirve para reentrenar las
  // DOS etapas, display y digitos, con fotos reales del taller)
  const capturarCuadro = useCallback((): string => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return '';
    try {
      const c = document.createElement('canvas');
      c.width = video.videoWidth;
      c.height = video.videoHeight;
      c.getContext('2d')!.drawImage(video, 0, 0);
      return c.toDataURL('image/jpeg', 0.7);
    } catch {
      return '';
    }
  }, []);

  // Subir la muestra etiquetada al dataset (fire-and-forget: si falla, nada)
  const guardarMuestra = useCallback((label: string, fuente: string) => {
    const image = capturarCuadro();
    if (!image || !label) return;
    axios.post('/api/vision/sample', {
      kind: 'odometro',
      label,
      lectura: ultimaCrudaRef.current,
      fuente,
      image
    }).catch(() => { /* dataset best-effort */ });
  }, [capturarCuadro]);

  // Intento FALLIDO: se cerro con un display a la vista pero sin km confirmado.
  // Foto completa sin etiqueta, para etiquetar en tanda y reentrenar.
  const detectadoRef = useRef(false);
  const guardarFallido = useCallback(() => {
    if (detectadoRef.current || !ultimaCrudaRef.current) return;
    const image = capturarCuadro();
    if (!image) return;
    axios.post('/api/vision/sample', {
      kind: 'odometro',
      label: '',
      lectura: ultimaCrudaRef.current,
      fuente: 'fallido',
      image
    }).catch(() => { /* dataset best-effort */ });
  }, [capturarCuadro]);

  const cerrar = useCallback(() => {
    guardarFallido();
    apagarTodo();
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardarFallido, apagarTodo]);

  // Aceptar un km a mano (la lectura tentativa o uno tipeado)
  const usarKm = useCallback((km: string, fuente: string) => {
    if (!km) return;
    detectadoRef.current = true;
    guardarMuestra(km, fuente);
    setLectura(km);
    setEstado('detectado');
    vivoRef.current = false;
    setTimeout(() => {
      onKm(Number(km));
      onClose();
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardarMuestra]);

  // La lectura tentativa ("¿123.456 km?") ya paso la plausibilidad: solo le
  // falto el segundo cuadro de confirmacion, un toque alcanza
  const confirmarManual = useCallback(() => usarKm(lectura, 'tentativa'), [lectura, usarKm]);

  const toggleLinterna = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await (track as any).applyConstraints({ advanced: [{ torch: !linternaPrendida }] });
      setLinternaPrendida(!linternaPrendida);
    } catch { /* algunos equipos no dejan */ }
  }, [linternaPrendida]);

  useEffect(() => {
    if (!open) { apagarTodo(); return; }
    let cancelado = false;

    (async () => {
      setEstado('cargando');
      setLectura('');
      setCuadro(null);
      ultimaLecturaRef.current = { km: '', veces: 0 };
      setManualAbierto(false);
      manualAbiertoRef.current = false;
      setManualValor('');
      ultimaCrudaRef.current = '';
      detectadoRef.current = false;
      rotacionRef.current = 0;
      sinDeteccionRef.current = 0;
      setZoom(1);
      zoomRef.current = 1;
      zoomDigitalRef.current = true;
      setZoomDigital(true);
      try {
        const [{ display, digitos }, stream] = await Promise.all([
          cargarSesiones(),
          navigator.mediaDevices.getUserMedia({
            // 1080p: el recorte del display sale mas nitido
            video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false
          })
        ]);
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        const caps = (track as any).getCapabilities?.();
        setConLinterna(Boolean(caps?.torch));
        // Botones de zoom segun el rango REAL de la camara
        if (caps?.zoom && Number.isFinite(caps.zoom.max) && caps.zoom.max > 1) {
          const niveles = [1, 2, 4, 8, 16, 25]
            .filter((n) => n >= (caps.zoom.min || 1) && n <= caps.zoom.max)
            .slice(0, 5);
          if (niveles.length >= 2) setNivelesZoom(niveles);
        } else {
          setNivelesZoom([1, 2, 4]);
        }
        const video = videoRef.current!;
        video.srcObject = stream;
        await video.play();
        setEstado('buscando');
        vivoRef.current = true;

        const ort = ortModulo;
        const canvas1 = document.createElement('canvas');
        canvas1.width = DISPLAY_SIZE;
        canvas1.height = DISPLAY_SIZE;
        const ctx1 = canvas1.getContext('2d', { willReadFrequently: true })!;
        const canvas2 = document.createElement('canvas');
        canvas2.width = DIGITOS_SIZE;
        canvas2.height = DIGITOS_SIZE;
        const ctx2 = canvas2.getContext('2d', { willReadFrequently: true })!;
        const input1 = new Float32Array(3 * DISPLAY_SIZE * DISPLAY_SIZE);
        const input2 = new Float32Array(3 * DIGITOS_SIZE * DIGITOS_SIZE);
        // Cuadro girado a resolucion completa (solo se usa con rotacion != 0)
        const canvasFrame = document.createElement('canvas');
        const ctxFrame = canvasFrame.getContext('2d', { willReadFrequently: true })!;

        // Un cuadro (x,y,w,h normalizado) leido sobre el frame girado, vuelto
        // a las coordenadas de lo que se ve en pantalla (el video sin girar)
        const mapearCuadro = (r: { x: number; y: number; w: number; h: number }) => {
          const rot = rotacionRef.current;
          if (rot === 0) return r;
          const pts = [[r.x, r.y], [r.x + r.w, r.y + r.h]].map(([x, y]) => {
            if (rot === 90) return [y, 1 - x];
            if (rot === 180) return [1 - x, 1 - y];
            return [1 - y, x]; // 270
          });
          const xs = [pts[0][0], pts[1][0]];
          const ys = [pts[0][1], pts[1][1]];
          return {
            x: Math.min(xs[0], xs[1]),
            y: Math.min(ys[0], ys[1]),
            w: Math.abs(xs[0] - xs[1]),
            h: Math.abs(ys[0] - ys[1])
          };
        };

        // Secuencia que vuelve a 0 entre giros: el caso comun (telefono
        // derecho) se prueba el doble de seguido que las otras orientaciones
        const SECUENCIA_ROT = [0, 90, 0, 180, 0, 270];
        let rotIdx = 0;

        const aTensor = (ctx: CanvasRenderingContext2D, size: number, dest: Float32Array) => {
          const px = ctx.getImageData(0, 0, size, size).data;
          const plano = size * size;
          for (let i = 0; i < plano; i++) {
            dest[i] = px[i * 4] / 255;
            dest[plano + i] = px[i * 4 + 1] / 255;
            dest[2 * plano + i] = px[i * 4 + 2] / 255;
          }
          return dest;
        };

        const ciclo = async () => {
          if (!vivoRef.current) return;
          try {
            const vw = video.videoWidth;
            const vh = video.videoHeight;
            if (!vw || !vh) { setTimeout(ciclo, 200); return; }

            // Fuente del cuadro: el video tal cual, o girado si el telefono
            // esta en otra orientacion
            const rot = rotacionRef.current;
            let fuente: CanvasImageSource = video;
            let fw = vw;
            let fh = vh;
            if (rot !== 0) {
              fw = rot === 180 ? vw : vh;
              fh = rot === 180 ? vh : vw;
              if (canvasFrame.width !== fw || canvasFrame.height !== fh) {
                canvasFrame.width = fw;
                canvasFrame.height = fh;
              }
              ctxFrame.save();
              ctxFrame.translate(fw / 2, fh / 2);
              ctxFrame.rotate((rot * Math.PI) / 180);
              ctxFrame.drawImage(video, -vw / 2, -vh / 2);
              ctxFrame.restore();
              fuente = canvasFrame;
            }

            // Zoom digital: se procesa (y se muestra) solo el centro del cuadro
            const z = zoomDigitalRef.current ? Math.max(1, zoomRef.current) : 1;
            const zw = fw / z;
            const zh = fh / z;
            const zx = (fw - zw) / 2;
            const zy = (fh - zh) / 2;

            // ── Etapa 1: display en el recorte visible (letterbox 640, gris 114) ──
            const r = Math.min(DISPLAY_SIZE / zw, DISPLAY_SIZE / zh);
            const nw = Math.round(zw * r);
            const nh = Math.round(zh * r);
            const dw = (DISPLAY_SIZE - nw) / 2;
            const dh = (DISPLAY_SIZE - nh) / 2;
            ctx1.fillStyle = 'rgb(114,114,114)';
            ctx1.fillRect(0, 0, DISPLAY_SIZE, DISPLAY_SIZE);
            ctx1.drawImage(fuente, zx, zy, zw, zh, dw, dh, nw, nh);
            const t1 = new ort.Tensor('float32', aTensor(ctx1, DISPLAY_SIZE, input1), [1, 3, DISPLAY_SIZE, DISPLAY_SIZE]);
            const o1 = await display.run({ [display.inputNames[0]]: t1 });
            const dets = decodeYolo(o1[display.outputNames[0]].data, 2, DISPLAY_ANCHORS, 0.3);

            if (!dets.length) {
              setCuadro(null);
              ultimaLecturaRef.current = { km: '', veces: 0 };
              // Nada a la vista: puede ser que el telefono este girado
              if (++sinDeteccionRef.current >= 2) {
                rotIdx = (rotIdx + 1) % SECUENCIA_ROT.length;
                rotacionRef.current = SECUENCIA_ROT[rotIdx];
                sinDeteccionRef.current = 0;
              }
              if (vivoRef.current) setTimeout(ciclo, 250);
              return;
            }
            sinDeteccionRef.current = 0;

            // a coordenadas del recorte visible, con 10% de margen (igual que el curado)
            const b = dets[0];
            const bx1 = Math.max(0, (b.x1 - dw) / r);
            const by1 = Math.max(0, (b.y1 - dh) / r);
            const bx2 = Math.min(zw, (b.x2 - dw) / r);
            const by2 = Math.min(zh, (b.y2 - dh) / r);
            // el overlay se dibuja sobre lo VISIBLE (que con zoom digital es el recorte)
            setCuadro(mapearCuadro({ x: bx1 / zw, y: by1 / zh, w: (bx2 - bx1) / zw, h: (by2 - by1) / zh }));
            const mx = (bx2 - bx1) * 0.1;
            const my = (by2 - by1) * 0.1;
            // coordenadas absolutas del cuadro para extraer el recorte de la etapa 2
            const cx1 = Math.max(0, zx + bx1 - mx);
            const cy1 = Math.max(0, zy + by1 - my);
            const cw = Math.min(fw - cx1, bx2 - bx1 + 2 * mx);
            const ch = Math.min(fh - cy1, by2 - by1 + 2 * my);
            if (cw < 8 || ch < 8) { if (vivoRef.current) setTimeout(ciclo, 250); return; }

            // ── Etapa 2: digitos sobre el recorte (letterbox 320) ──
            const r2 = Math.min(DIGITOS_SIZE / cw, DIGITOS_SIZE / ch);
            const nw2 = Math.round(cw * r2);
            const nh2 = Math.round(ch * r2);
            const dw2 = (DIGITOS_SIZE - nw2) / 2;
            const dh2 = (DIGITOS_SIZE - nh2) / 2;
            ctx2.fillStyle = 'rgb(114,114,114)';
            ctx2.fillRect(0, 0, DIGITOS_SIZE, DIGITOS_SIZE);
            ctx2.drawImage(fuente, cx1, cy1, cw, ch, dw2, dh2, nw2, nh2);
            const t2 = new ort.Tensor('float32', aTensor(ctx2, DIGITOS_SIZE, input2), [1, 3, DIGITOS_SIZE, DIGITOS_SIZE]);
            const o2 = await digitos.run({ [digitos.inputNames[0]]: t2 });
            const chars = decodeYolo(o2[digitos.outputNames[0]].data, 12, DIGITOS_ANCHORS, 0.3)
              .map((d) => ({ x: d.x1, x2: d.x2, w: d.x2 - d.x1, cy: (d.y1 + d.y2) / 2, h: d.y2 - d.y1, ch: CLASES_DIGITOS[d.cls] }));
            const km = leerKilometraje(chars);
            ultimaCrudaRef.current = km;

            if (esKmPlausible(km, kmReferencia)) {
              const ult = ultimaLecturaRef.current;
              const veces = ult.km === km ? ult.veces + 1 : 1;
              ultimaLecturaRef.current = { km, veces };
              setLectura(km);
              // dos lecturas iguales seguidas = sugerencia confirmada
              // (salvo que el operario este tipeando a mano: no cerrarle el teclado)
              if (veces >= 2 && !manualAbiertoRef.current) {
                detectadoRef.current = true;
                guardarMuestra(km, 'auto');
                setEstado('detectado');
                vivoRef.current = false;
                setTimeout(() => {
                  onKm(Number(km));
                  onClose();
                }, 700);
                return;
              }
            }
          } catch (e) {
            // un frame que falla no corta el ciclo
            console.warn('[OdometerScanner] frame error', e);
          }
          if (vivoRef.current) setTimeout(ciclo, 250);
        };
        ciclo();
      } catch (e: any) {
        console.error('[OdometerScanner] init error', e);
        if (!cancelado) {
          setEstado('error');
          setMensajeError(
            e?.name === 'NotAllowedError'
              ? 'No hay permiso para usar la cámara. Habilitalo en el navegador y reintentá.'
              : 'No se pudo iniciar el escáner. Verificá la conexión (los modelos se descargan la primera vez).'
          );
        }
      }
    })();

    return () => { cancelado = true; apagarTodo(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { cerrar(); } }}>
      <DialogContent className="max-w-lg overflow-hidden p-0" hideCloseButton onOpenAutoFocus={(e: any) => e.preventDefault()}>
        <div className="relative overflow-hidden bg-black" style={{ minHeight: 320 }}>
          <video
            ref={videoRef}
            playsInline
            muted
            className="block h-auto w-full"
            style={{
              maxHeight: '70vh',
              transform: zoomDigital && zoom > 1 ? `scale(${zoom})` : undefined,
              transformOrigin: 'center center'
            }}
          />

          {cuadro && (
            <div
              className={`absolute rounded border-2 ${estado === 'detectado' ? 'border-green-400' : 'border-yellow-300'}`}
              style={{
                left: `${cuadro.x * 100}%`,
                top: `${cuadro.y * 100}%`,
                width: `${cuadro.w * 100}%`,
                height: `${cuadro.h * 100}%`
              }}
            />
          )}

          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
            <span className="rounded-full bg-black/60 px-3 py-1 text-sm text-white">
              {estado === 'cargando' && 'Preparando escáner...'}
              {estado === 'buscando' && (lectura ? `¿${Number(lectura).toLocaleString('es-AR')} km?` : 'Apuntá al odómetro')}
              {estado === 'detectado' && `${Number(lectura).toLocaleString('es-AR')} km`}
              {estado === 'error' && 'Error'}
            </span>
            <div className="flex items-center gap-2">
              {estado !== 'detectado' && (
                <button
                  type="button"
                  onClick={() => {
                    // Editor precargado con lo leido: solo se corrige el digito equivocado
                    setManualValor(String(lectura || ultimaCrudaRef.current || '').replace(/[^0-9]/g, '').slice(0, 7));
                    setManualAbierto(true);
                    manualAbiertoRef.current = true;
                  }}
                  className="rounded-full bg-black/60 p-2 text-white"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}
              {conLinterna && (
                <button
                  type="button"
                  onClick={toggleLinterna}
                  className={`rounded-full p-2 ${linternaPrendida ? 'bg-yellow-400 text-black' : 'bg-black/60 text-white'}`}
                >
                  <BoltIcon className="h-5 w-5" />
                </button>
              )}
              <button type="button" onClick={() => { cerrar(); }} className="rounded-full bg-black/60 p-2 text-white">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Lectura tentativa: un toque la acepta sin esperar la confirmacion */}
          {estado === 'buscando' && lectura && (
            <div className="absolute inset-x-0 bottom-16 flex justify-center">
              <button
                type="button"
                onClick={confirmarManual}
                className="rounded-xl border-2 border-green-400 bg-black/70 px-6 py-2 text-xl font-bold tracking-widest text-white active:bg-green-500"
              >
                Usar {Number(lectura).toLocaleString('es-AR')} km
              </button>
            </div>
          )}

          {/* Zoom para tableros lejanos */}
          {estado === 'buscando' && (
            <div className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-black/60 p-1">
              {nivelesZoom.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => aplicarZoom(n)}
                  className={`rounded-full px-3 py-1 text-sm font-medium ${zoom === n ? 'bg-white text-black' : 'text-white'}`}
                >
                  {n}x
                </button>
              ))}
            </div>
          )}

          {estado === 'detectado' && (
            <div className="absolute inset-x-0 bottom-0 flex justify-center p-4">
              <span className="rounded-xl bg-green-500 px-6 py-2 text-xl font-bold tracking-widest text-white">
                {Number(lectura).toLocaleString('es-AR')} km
              </span>
            </div>
          )}

          {estado === 'error' && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <p className="text-center text-sm text-white">{mensajeError}</p>
            </div>
          )}

          {/* Carga manual: no detecta, o detecta cualquier cosa */}
          {manualAbierto && estado !== 'detectado' && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70 p-4">
              <div className="w-full max-w-xs space-y-2 rounded-xl bg-black/80 p-4">
                <p className="text-center text-sm text-white">Escribí los km a mano (queda para mejorar el escáner)</p>
                <input
                  autoFocus
                  inputMode="numeric"
                  value={manualValor}
                  onChange={(e) => setManualValor(e.target.value.replace(/\D/g, '').slice(0, 7))}
                  placeholder="85000"
                  className="w-full rounded-lg border border-white/30 bg-black/60 px-3 py-2 text-center text-2xl font-bold tracking-widest text-white outline-none focus:border-green-400"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setManualAbierto(false); manualAbiertoRef.current = false; }}
                    className="flex-1 rounded-lg bg-white/10 px-3 py-2 text-sm text-white"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={!manualValor}
                    onClick={() => usarKm(manualValor, 'manual')}
                    className="flex-1 rounded-lg bg-green-500 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
                  >
                    Usar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Boton de camara + escaner listo para poner al lado de cualquier campo de km */
export const OdometerScanButton: React.FC<{ onKm: (km: number) => void; kmReferencia?: number; className?: string }> = ({ onKm, kmReferencia, className }) => {
  const [abierto, setAbierto] = useState(false);
  // Solo en celular o tablet: en la computadora el km se escribe (Rodrigo,
  // 16/10/2026). Y sin cámara el botón no sirve. En los dos casos se oculta y
  // no se precargan los modelos.
  const [conCamara, setConCamara] = useState(() => esCelularOTablet());
  useEffect(() => {
    if (!esCelularOTablet()) return undefined;
    let vivo = true;
    let t: any = null;
    const armar = () => { t = setTimeout(precalentarEscanerOdometro, 800); };
    if (navigator.mediaDevices?.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices()
        .then((ds) => {
          if (!vivo) return;
          const hay = ds.some((d) => d.kind === 'videoinput');
          setConCamara(hay);
          if (hay) armar();
        })
        .catch(() => { if (vivo) armar(); }); // en la duda, mostrar
    } else {
      setConCamara(false);
    }
    return () => { vivo = false; if (t) clearTimeout(t); };
  }, []);
  if (!conCamara) return null;
  return (
    <>
      <Tip label="Leer el kilometraje con la cámara">
        <Button type="button" variant="outline" size="sm" onClick={() => setAbierto(true)} className={className}>
          <CameraIcon className="h-4 w-4" />
        </Button>
      </Tip>
      <OdometerScanner open={abierto} onClose={() => setAbierto(false)} onKm={onKm} kmReferencia={kmReferencia} />
    </>
  );
};

export default OdometerScanner;
